import React, { useContext, useEffect, useState } from 'react';
import FilterDropdownType from '../../enums/filterDropdownType.enum.js';
import { Button, Tooltip, message, Modal } from 'antd'
import { listProducts, removeProduct } from "../../services/product.service"
import { AdminProductModal } from '../adminProductModal/adminProductModal.component.js';
import { EditOutlined, DeleteOutlined, CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons'
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import moment from 'moment'
import "./adminProductsTable.scss"
import UrlHelper from '../../helpers/url.helper.js';
import { PageHeader } from '../pageHeader/pageHeader.component.js';
import { useParams } from 'react-router-dom';
import { FlexibleTable } from '../flexibleTable/flexibleTable.component.js';
import { UserContext } from '../../contexts/user.context.js';
import ChargeStatus from '../../enums/chargeStatus.enum.js';
dayjs.extend(utc)
dayjs.extend(timezone)

const select = '_id createdAt patient productType status hasKit tracking trackingNumber hasAppointment facility start shippingLocation charge'
const populate = [{
  path: 'patient',
  select: '_id firstName lastName'
}, {
  path: 'productType',
  select: 'title code'
}, {
  path: 'facility',
  select: 'name location'
}, {
  path: 'charge',
  select: 'status originalCost amount'
}]

export const AdminProductsTable = ({ hasPatient=false }) => {
  const { patientId } = useParams()
  const [isLoading, setIsLoading] = useState(true)
  const [openAdminProductModal, setOpenAdminProductModal] = useState()
  const [products, setProducts] = useState([])
  const { currentUser, setCounts } = useContext(UserContext)
  const [productId, setProductId] = useState()
  const [filteredCount, setFilteredCount] = useState();
  const [productTypeTitles, setProductTypeTitles] = useState([])

  useEffect(() => {
    document.title = 'Instalab | Products'
  }, [])

  useEffect(() => {
    fetchProducts()
  }, [hasPatient, patientId])

  const fetchProducts = async () => {
    setIsLoading(true)
    if (hasPatient && !patientId) return

    let params = {
      select,
      filter: {
        isRemoved: {
          $ne: true
        },
      },
      populate,
      sort: '-createdAt'
    }

    if (hasPatient) {
      params.filter = {
        patient: patientId
      }
    } else {
      params.select = `${params.select} patient`
      params.populate = [
        ...params.populate,
        {
          path: 'patient',
          select: '_id firstName lastName'
        }
      ]
    }

    const fetchedProducts = await listProducts(params)
    setProductTypeTitles([...new Set(fetchedProducts.filter(({ productType }) => productType).map(({ productType }) => productType.title))].sort())
    setProducts(fetchedProducts)
    setFilteredCount(fetchedProducts.length)
    setIsLoading(false)
  }


  const onProductUpdate = (newProducts) => {
    for (const newProduct of newProducts) {
    if (products.some(p => p._id === newProduct._id)) {
      setProducts(cachedProducts => {
        return cachedProducts.map(p => p._id === newProduct._id ? newProduct : p)
      })
    } else {
      setProducts(cachedProducts => {
        return [
          newProduct,
          ...cachedProducts
        ]
      })
      setCounts(cachedCounts => {
        return {
          ...cachedCounts,
          products: cachedCounts.products + 1
        }
      })
        setFilteredCount(cachedFilterCount => cachedFilterCount+1)
      }
    }
  }

  const onRemove = (_id) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this product?',
      content: 'This action cannot be undone.',
      okText: 'Yes, delete it',
      okType: 'danger',
      cancelText: 'No, keep it',
      onOk: async () => {
        try {
          await removeProduct(_id);
          setProducts(products.filter(product => product._id !== _id));
          setCounts(cachedCounts => {
            return {
              ...cachedCounts,
              products: cachedCounts.products - 1
            }
          })
          setFilteredCount(cachedFilterCount => cachedFilterCount-1)
          message.info('Product removed');
        } catch (err) {
          message.error('Failed to remove product');
        }
      }
    });
  }

  const getActionItems = (p) => {
    return [
      {
        key: 'tracking-number',
        label: (
          <Button onClick={() => {
            setProductId(p._id)
            setOpenAdminProductModal(true)
          }}>
            <EditOutlined /> Edit Product
          </Button>
        )
      }, {
        type: 'divider'
      }, {
        key: 'delete',
        label: (
          <Button 
            onClick={() => onRemove(p._id)}
            className="remove-item"
          >
            <DeleteOutlined /> Remove
          </Button>
        ),
        adminOnly: true
      }
    ]
  }

  const onAddProduct = () => {
    setOpenAdminProductModal(true)
  }

  const getCustomFilter = (product, value) => {
    return {
      patient: () => product.patient && `${product.patient.firstName} ${product.patient.lastName}`.toLowerCase().includes(value.toLowerCase()),
      productType: () => product.productType?.title === value
    }
  }

  return (
    <div className="admin-products-table">
      <AdminProductModal
        productId={productId}
        setProductId={setProductId}
        open={openAdminProductModal}
        setOpen={setOpenAdminProductModal}
        onSuccess={onProductUpdate}
        patientId={patientId}
        select={select}
        populate={populate}
      />

      <PageHeader
        title="Products"
        count={filteredCount}
        actions={
          <Button type="primary" onClick={onAddProduct}>
            + Add Product
          </Button>
        }
      />

      <FlexibleTable
        isLoading={isLoading}
        records={products}
        setFilteredCount={setFilteredCount}
        getCustomFilter={getCustomFilter}
        getActionItems={getActionItems}
        columns={[


        {
          title: 'Ordered',
          dataIndex: 'createdAt',
          render: createdAt => (
            <Tooltip title={`${Math.round(moment().diff(moment(createdAt), 'weeks', true)*10)/10} weeks ago`}>
              {moment(createdAt).format('MM/DD/YY')}
            </Tooltip>
          )
        },
        !hasPatient && {
          title: 'Patient',
          dataIndex: 'patient',
          filterDropdownType: FilterDropdownType.INPUT,
          render: patient => patient ? (
            <a
              className="link"
              onClick={() => {
                window.open(UrlHelper.getPatientProfile(patient._id), '_blank');
              }}
            >
              {patient.firstName} {patient.lastName}
            </a>
          ) : `n/a`
        }, {
          title: 'Type',
          dataIndex: 'productType',
          render: (_, record) => record.productType ? <>{record.productType.title}</> : <>{record.type}</>,
          filterDropdownType: FilterDropdownType.CHECKBOX,
          filterOptions: productTypeTitles
        },
        {
          title: "Has Kit",
          dataIndex: "hasKit",
          render: hasKit => hasKit ? <CheckCircleFilled style={{ color: '#52c41a' }} /> : <CloseCircleFilled style={{ color: '#ff4d4f' }} />
        },
        {
          title: "Has Appointment",
          dataIndex: "hasAppointment",
          render: hasAppointment => hasAppointment ? <CheckCircleFilled style={{ color: '#52c41a' }} /> : <CloseCircleFilled style={{ color: '#ff4d4f' }} />
        }, {
          title: "Charge Status",
          dataIndex: "charge",
          render: charge => charge?.status === ChargeStatus.PAID ? `Paid: $${charge?.amount}` : charge?.status
        }
        ]}
      />
    </div>
  )
}